import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface CategoryCardProps {
  image: string;
  title: string;
  tagline: string;
  productCount?: number;
  badge?: string;
  ctaText?: string;
  link: string;
  featured?: boolean;
  className?: string;
}

export function CategoryCard({
  image,
  title,
  tagline,
  productCount,
  badge,
  ctaText = "Shop Now",
  link,
  featured = false,
  className,
}: CategoryCardProps) {
  return (
    <Link
      to={link}
      className={cn(
        "group relative overflow-hidden rounded-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:ring-2 hover:ring-ring",
        "bg-background border border-border",
        "focus:outline-none focus:ring-2 focus:ring-ring focus:scale-[1.02]",
        featured && "md:col-span-2",
        className
      )}
    >
      {/* Image Container */}
      <div className={cn(
        "relative overflow-hidden",
        featured ? "aspect-[8/3]" : "aspect-[4/3]"
      )}>
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        
        {/* Badge */}
        {badge && (
          <div className="absolute top-4 right-4 bg-primary/90 backdrop-blur-sm text-primary-foreground px-3 py-1.5 rounded-full text-sm font-medium">
            {badge}
          </div>
        )}
      </div>

      {/* Content Overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
        <h3 className={cn(
          "font-bold mb-2 transition-colors",
          featured ? "text-3xl md:text-4xl" : "text-2xl md:text-3xl"
        )}>
          {title}
        </h3>
        
        <p className={cn(
          "text-white/90 mb-3",
          featured ? "text-base md:text-lg" : "text-sm md:text-base"
        )}>
          {tagline}
        </p>
        
        {productCount !== undefined && (
          <p className="text-white/80 text-sm mb-4">
            {productCount} {productCount === 1 ? 'product' : 'products'}
          </p>
        )}
        
        {/* CTA */}
        <div className="inline-flex items-center gap-2 text-white font-medium group-hover:gap-3 transition-all">
          <span>{ctaText}</span>
          <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </Link>
  );
}
